import axios from "axios";

// Call the API on the SAME ORIGIN the page is served from, so the app works on any
// domain (www.yumaera.com / yumaera.com / preview) regardless of the baked backend URL,
// keeping session cookies first-party. Falls back to REACT_APP_BACKEND_URL off-browser.
const ORIGIN = typeof window !== "undefined" && window.location?.origin
  ? window.location.origin
  : (process.env.REACT_APP_BACKEND_URL || "");
export const API = `${ORIGIN}/api`;

// Cookies (session_token) must ride along with every request.
const client = axios.create({ baseURL: API, timeout: 25000, withCredentials: true });

export const api = {
  // ---- auth ----
  getMe: () => client.get("/auth/me").then((r) => r.data),
  createSession: (sessionId) =>
    client.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } }).then((r) => r.data),
  acceptTerms: () => client.post("/auth/accept-terms").then((r) => r.data),
  getWsTicket: () => client.get("/auth/ws-ticket").then((r) => r.data),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  googleVerify: (credential) => client.post("/auth/google/verify", { credential }).then((r) => r.data),
  // owner: user management
  listUsers: () => client.get("/auth/users").then((r) => r.data),
  getAudit: () => client.get("/auth/audit").then((r) => r.data),
  setRole: (userId, role) => client.post(`/auth/users/${userId}/role`, { role }).then((r) => r.data),
  suspendUser: (userId) => client.post(`/auth/users/${userId}/suspend`).then((r) => r.data),
  restoreUser: (userId) => client.post(`/auth/users/${userId}/restore`).then((r) => r.data),
  // owner: workspace keys
  getWorkspaceKeys: () => client.get("/auth/workspace-keys").then((r) => r.data),
  setWorkspaceKey: (provider, secret) =>
    client.post("/auth/workspace-keys", { provider, secret }).then((r) => r.data),
  delWorkspaceKey: (provider) => client.delete(`/auth/workspace-keys/${provider}`).then((r) => r.data),
  // personal api access
  getPersonalRequests: () => client.get("/auth/personal/requests").then((r) => r.data),
  createPersonalRequest: (provider, purpose) =>
    client.post("/auth/personal/requests", { provider, purpose }).then((r) => r.data),
  decideRequest: (rid, decision) =>
    client.post(`/auth/personal/requests/${rid}/decision`, { decision }).then((r) => r.data),
  getPersonalKeys: () => client.get("/auth/personal/keys").then((r) => r.data),
  addPersonalKey: (provider, secret) =>
    client.post("/auth/personal/keys", { provider, secret }).then((r) => r.data),

  // pairs
  getPairs: () => client.get("/pairs").then((r) => r.data),
  addPair: (symbol, color) => client.post("/pairs", { symbol, color }).then((r) => r.data),
  deletePair: (id) => client.delete(`/pairs/${id}`).then((r) => r.data),
  reorderPairs: (order) => client.put("/pairs/reorder", { order }).then((r) => r.data),
  // market
  getTicker: (symbol) => client.get("/market/ticker", { params: { symbol } }).then((r) => r.data),
  getKlines: (symbol, interval, limit = 300) =>
    client.get("/market/klines", { params: { symbol, interval, limit } }).then((r) => r.data),
  getOverview: (interval = "1h") =>
    client.get("/market/overview", { params: { interval } }).then((r) => r.data),
  // analysis
  getAnalysis: (symbol, interval = "1h") =>
    client.get("/analysis", { params: { symbol, interval } }).then((r) => r.data),
  // plans
  savePlan: (symbol, interval) => client.post("/plans", { symbol, interval }).then((r) => r.data),
  getPlans: () => client.get("/plans").then((r) => r.data),
  deletePlan: (id) => client.delete(`/plans/${id}`).then((r) => r.data),
  getPlanHistory: () => client.get("/plan-history").then((r) => r.data),
  getPlanHistoryStats: () => client.get("/plan-history/stats").then((r) => r.data),
  // swing setups
  getSwing: (symbol) => client.get("/swing", { params: { symbol } }).then((r) => r.data),
  generateSwing: (symbol) => client.post("/swing/generate", { symbol }).then((r) => r.data),
  cancelSwing: (planId, reason) => client.post(`/swing/${planId}/cancel`, { reason }).then((r) => r.data),
  activateCandidate: (planId) => client.post(`/swing/${planId}/activate-candidate`).then((r) => r.data),
  getSwingHistory: (symbol) => client.get("/swing/history", { params: symbol ? { symbol } : {} }).then((r) => r.data),
  // paper
  getPositions: () => client.get("/paper/positions").then((r) => r.data),
  openPosition: (body) => client.post("/paper/positions", body).then((r) => r.data),
  closePosition: (id) => client.post(`/paper/positions/${id}/close`).then((r) => r.data),
  // journal
  getJournal: () => client.get("/journal").then((r) => r.data),
  addJournal: (body) => client.post("/journal", body).then((r) => r.data),
  delJournal: (id) => client.delete(`/journal/${id}`).then((r) => r.data),
  // performance
  getPerformance: () => client.get("/performance").then((r) => r.data),
  // settings
  getSettings: () => client.get("/settings").then((r) => r.data),
  updateSettings: (body) => client.put("/settings", body).then((r) => r.data),
  // providers / intel
  getProviderStatus: () => client.get("/providers/status").then((r) => r.data),
  getIntelligence: () => client.get("/intelligence").then((r) => r.data),
  getMarketOutlook: (symbol, deepDive = false) => client.post("/ai/market-outlook", { symbol, deepDive }).then((r) => r.data),
  getOutlookAlerts: () => client.get("/alerts/outlook").then((r) => r.data),
  markOutlookAlertsRead: () => client.post("/alerts/outlook/read").then((r) => r.data),
};

export default api;
